import { api } from "@/lib/api";
import type { AuthUser } from "@/lib/auth";

export type PlatformMeta = { page: number; per_page: number; total: number };

export type PlatformUserRow = {
  id: number;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  platform_role: string | null;
  online: boolean;
  last_activity_at: string | null;
  last_login_at: string | null;
  created_at: string;
};

export type PlatformAccountRow = {
  id: number;
  name: string;
  owner_user_id: number;
  owner_label: string;
  locked: boolean;
  memberships_count: number;
  brands_count: number;
  created_at: string;
  enterprise_verified_at: string | null;
};

export type PlatformBrandRow = {
  id: number;
  name: string;
  account_id: number;
  account_name: string;
  discarded_at: string | null;
  discarded: boolean;
  created_at: string;
};

export type ImpersonationMeta = {
  actor_user_id: number;
  actor_label: string;
  target_user_id: number;
  target_label: string;
};

export type ImpersonationInfo = ImpersonationMeta;

export type ImpersonateSession = {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  user: AuthUser;
  impersonation: ImpersonationMeta;
};

function appendParam(sp: URLSearchParams, key: string, value: string | number | undefined) {
  if (value === undefined || value === "") return;
  sp.set(key, String(value));
}

export function fetchPlatformUsers(params: {
  q?: string;
  online?: "true" | "false" | "";
  page?: number;
  per_page?: number;
}) {
  const sp = new URLSearchParams();
  appendParam(sp, "q", params.q);
  appendParam(sp, "online", params.online);
  appendParam(sp, "page", params.page);
  appendParam(sp, "per_page", params.per_page);
  const qs = sp.toString();
  return api.fetch<{ users: PlatformUserRow[]; meta: PlatformMeta }>(
    `/platform/users${qs ? `?${qs}` : ""}`,
  );
}

export function impersonatePlatformUser(userId: number) {
  return api.fetch<ImpersonateSession>(`/platform/users/${userId}/impersonate`, {
    method: "POST",
  });
}

export function fetchPlatformAccounts(params: {
  q?: string;
  locked?: "true" | "false" | "";
  page?: number;
  per_page?: number;
}) {
  const sp = new URLSearchParams();
  appendParam(sp, "q", params.q);
  appendParam(sp, "locked", params.locked);
  appendParam(sp, "page", params.page);
  appendParam(sp, "per_page", params.per_page);
  const qs = sp.toString();
  return api.fetch<{ accounts: PlatformAccountRow[]; meta: PlatformMeta }>(
    `/platform/accounts${qs ? `?${qs}` : ""}`,
  );
}

export function fetchPlatformBrands(params: {
  q?: string;
  discarded?: "true" | "false" | "";
  page?: number;
  per_page?: number;
}) {
  const sp = new URLSearchParams();
  appendParam(sp, "q", params.q);
  appendParam(sp, "discarded", params.discarded);
  appendParam(sp, "page", params.page);
  appendParam(sp, "per_page", params.per_page);
  const qs = sp.toString();
  return api.fetch<{ brands: PlatformBrandRow[]; meta: PlatformMeta }>(
    `/platform/brands${qs ? `?${qs}` : ""}`,
  );
}

export type PlatformTemplateRow = {
  id: number;
  name: string;
  source_key: string;
  listed: boolean;
  imported?: boolean;
  public_url: string | null;
  category?: { slug: string; name: string } | null;
  brand_site?: {
    id: number;
    account_id?: number;
    brand_id?: number;
    slug?: string;
    purpose?: string;
  } | null;
  preview_status?: "missing" | "stale" | "fresh" | "queued" | "running" | "failed" | string;
  preview_fresh?: boolean;
  preview_captured_at?: string | null;
  preview_capture_error?: string | null;
  has_preview_image?: boolean;
  needs_preview_capture?: boolean;
};

export type PlatformTemplatesResponse = {
  templates: PlatformTemplateRow[];
  preview_capture?: { busy?: boolean; concurrency?: number };
};

export function fetchPlatformTemplates() {
  return api.fetch<PlatformTemplatesResponse>("/platform/templates");
}

export function updatePlatformTemplateListed(sourceKey: string, listed: boolean) {
  return api.fetch<{ template: PlatformTemplateRow }>(
    `/platform/templates/${encodeURIComponent(sourceKey)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ listed }),
    },
  );
}

export function importPlatformTemplate(sourceKey: string) {
  return api.fetch<{ template: PlatformTemplateRow }>(
    `/platform/templates/${encodeURIComponent(sourceKey)}/import`,
    { method: "POST" },
  );
}

export function capturePlatformTemplatePreview(sourceKey: string, force = false) {
  return api.fetch<{
    template: PlatformTemplateRow;
    preview_capture?: { busy?: boolean; concurrency?: number; enqueued?: string[] };
  }>(`/platform/templates/${encodeURIComponent(sourceKey)}/capture_preview`, {
    method: "POST",
    body: JSON.stringify({ force }),
  });
}

export function capturePlatformTemplatePreviews(opts?: {
  force?: boolean;
  source_keys?: string[];
}) {
  return api.fetch<{
    enqueued: string[];
    skipped: { source_key: string; reason: string }[];
    preview_capture?: { busy?: boolean; concurrency?: number };
  }>("/platform/templates/capture_previews", {
    method: "POST",
    body: JSON.stringify({
      force: opts?.force ?? false,
      source_keys: opts?.source_keys,
    }),
  });
}
